import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, CTA, PRICE, NAV_LINKS } from "../config/brand";
import { PurchaseButton } from "./PurchaseButton";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const lenis = window.__lenis;
  if (lenis) lenis.scrollTo(el, { offset: -72, duration: 1.2 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const Anchor = ({ href, onClick, className, children, testId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const handle = (e) => {
    e.preventDefault();
    const id = href.replace("#", "");
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 350);
    } else {
      scrollToId(id);
    }
    onClick?.();
  };
  return (
    <a href={href} onClick={handle} data-testid={testId} className={className}>
      {children}
    </a>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "border-mauve/30 bg-ivory/90 shadow-[0_4px_24px_rgba(90,48,73,0.07)] backdrop-blur-xl"
          : "border-transparent bg-ivory"
      }`}
    >
      <div className="mx-auto flex h-[68px] max-w-[1280px] items-center justify-between px-5 sm:px-8">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <img src="/assets/logo-mark.png" alt="" className="h-8 w-auto" width={32} height={42} />
          <span className="font-serif text-[1.45rem] font-semibold tracking-tight text-plum">
            {BRAND.name}
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <Anchor
              key={l.href}
              href={l.href}
              testId={`nav-link-${l.href.slice(1)}`}
              className="relative text-[12px] font-semibold uppercase tracking-[0.12em] text-plumcharcoal/65 transition-colors after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-gold after:transition-transform after:duration-300 hover:text-plum hover:after:scale-x-100"
            >
              {l.label}
            </Anchor>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <PurchaseButton
            event="nav_cta_click"
            testId="nav-get-system-button"
            className="hidden rounded-xl bg-plum px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.12em] text-ivory transition-colors hover:bg-plum/90 sm:inline-flex"
          >
            {CTA.primaryShort}
          </PurchaseButton>
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-plum transition-colors hover:bg-blushcream lg:hidden"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-mauve/25 bg-ivory lg:hidden"
            aria-label="Mobile"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV_LINKS.map((l) => (
                <Anchor
                  key={l.href}
                  href={l.href}
                  testId={`nav-mobile-link-${l.href.slice(1)}`}
                  onClick={() => setOpen(false)}
                  className="rounded-xl px-3 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-plumcharcoal/75 transition-colors hover:bg-blushcream hover:text-plum"
                >
                  {l.label}
                </Anchor>
              ))}
              <div className="px-3 pb-2 pt-3">
                <PurchaseButton
                  event="mobile_cta_click"
                  testId="nav-mobile-get-system-button"
                  className="w-full rounded-xl bg-rose px-5 py-4 text-[12px] font-bold uppercase tracking-[0.12em] text-warmwhite transition-colors hover:bg-[#b96f84]"
                >
                  {CTA.primaryShort} — {PRICE}
                </PurchaseButton>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
